import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Star, 
  Clock, 
  TrendingUp, 
  MoreVertical, 
  FileText, 
  Eye, 
  Download, 
  Zap, 
  Mail, 
  Users, 
  MessageSquare,
  MessageCircle,
  Database,
  Search,
  Code,
  Headphones,
  type LucideIcon 
} from 'lucide-react';
import { toast } from 'sonner';
import { TemplatesPageSkeleton } from '@/components/ui/page-skeletons';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: number;
  useCount: number;
  rating: number;
  isFeatured: boolean;
  tags: string[];
}

// Icon mapping for template icons
const iconMap: Record<string, LucideIcon> = {
  FileText,
  Zap,
  Mail,
  Users,
  MessageSquare,
  MessageCircle,
  Database,
  Search,
  Code,
  Headphones,
};

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [useTemplateDialog, setUseTemplateDialog] = useState<Template | null>(null);
  const [workflowName, setWorkflowName] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templatesApi.list();
      setTemplates(response.data.data || []);
    } catch (error: any) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (template: Template) => {
    try {
      const response = await templatesApi.get(template.id);
      setPreviewTemplate(response.data.data || response.data);
    } catch (error: any) {
      toast.error('Failed to load template details');
    }
  };

  const handleUseTemplate = (template: Template) => {
    setUseTemplateDialog(template);
    setWorkflowName(`${template.name} (Copy)`);
  };

  const confirmUseTemplate = async () => {
    if (!useTemplateDialog || !workflowName.trim()) return;

    try {
      const response = await templatesApi.use(useTemplateDialog.id, workflowName);
      toast.success('Workflow created from template!');
      setUseTemplateDialog(null);
      setWorkflowName('');
      const workflowData = response.data.data || response.data;
      navigate(`/workflows/${workflowData.id}`);
    } catch (error: any) {
      toast.error('Failed to create workflow from template');
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'intermediate':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'advanced':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = iconMap[iconName] || FileText;
    return IconComponent;
  };

  if (loading) {
    return <TemplatesPageSkeleton />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Workflow Templates</h1>
          <p className="text-muted-foreground mt-1">
            Start with pre-built workflows and customize them to your needs
          </p>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No templates available</h3>
          <p className="text-muted-foreground mb-4">
            Check back later for workflow templates
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%] min-w-[300px]">Template</TableHead>
                  <TableHead className="w-[15%] min-w-[120px]">Category</TableHead>
                  <TableHead className="w-[15%] min-w-[120px]">Difficulty</TableHead>
                  <TableHead className="w-[10%] min-w-[80px]">Time</TableHead>
                  <TableHead className="w-[10%] min-w-[80px]">Uses</TableHead>
                  <TableHead className="w-[10%] min-w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => {
                  const IconComponent = getIconComponent(template.icon);
                  return (
                  <TableRow key={template.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="max-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10">
                          <IconComponent className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{template.name}</span>
                            {template.isFeatured && (
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {template.description}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="whitespace-nowrap">
                        {template.category.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`whitespace-nowrap ${getDifficultyColor(template.difficulty)}`}>
                        {template.difficulty}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                        <Clock className="h-4 w-4" />
                        <span>{template.estimatedTime}m</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                        <TrendingUp className="h-4 w-4" />
                        <span>{template.useCount}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleUseTemplate(template)}>
                            <Download className="w-4 h-4 mr-2" />
                            Use Template
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePreview(template)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {previewTemplate && (() => {
                const PreviewIcon = getIconComponent(previewTemplate.icon);
                return (
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-500/10">
                    <PreviewIcon className="h-6 w-6 text-purple-600" />
                  </div>
                );
              })()}
              <div>
                <div className="flex items-center gap-2">
                  {previewTemplate?.name}
                  {previewTemplate?.isFeatured && (
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {previewTemplate?.category.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${previewTemplate && getDifficultyColor(previewTemplate.difficulty)}`}>
                    {previewTemplate?.difficulty}
                  </Badge>
                </div>
              </div>
            </DialogTitle>
            <DialogDescription className="text-base mt-4">
              {previewTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Estimated Time:</span>
                <span className="font-medium">{previewTemplate?.estimatedTime} minutes</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Times Used:</span>
                <span className="font-medium">{previewTemplate?.useCount}</span>
              </div>
            </div>

            {previewTemplate?.tags && previewTemplate.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {previewTemplate.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground">
                This template includes pre-configured nodes and connections to help you get started quickly.
                Click "Use Template" to create a new workflow based on this template.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
              Close
            </Button>
            <Button onClick={() => {
              if (previewTemplate) {
                setPreviewTemplate(null);
                handleUseTemplate(previewTemplate);
              }
            }}>
              <Download className="w-4 h-4 mr-2" />
              Use Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={!!useTemplateDialog} onOpenChange={() => setUseTemplateDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Workflow from Template</DialogTitle>
            <DialogDescription>
              Enter a name for your new workflow. You can customize it after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workflow-name">Workflow Name</Label>
              <Input
                id="workflow-name"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="Enter workflow name..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    confirmUseTemplate();
                  }
                }}
              />
            </div>

            {useTemplateDialog && (() => {
              const UseIcon = getIconComponent(useTemplateDialog.icon);
              return (
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10">
                    <UseIcon className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{useTemplateDialog.name}</p>
                    <p className="text-sm text-muted-foreground">{useTemplateDialog.category}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {useTemplateDialog.description}
                </p>
              </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUseTemplateDialog(null)}>
              Cancel
            </Button>
            <Button onClick={confirmUseTemplate} disabled={!workflowName.trim()}>
              <Download className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
